/**
 * ApiKeysOverviewTab — the super-admin global control surface for tenant
 * AI provider keys. Renders:
 *  - an incident banner when any key is unhealthy (the in-dashboard twin
 *    of the Slack channel alert), and
 *  - a table of EVERY tenant key across the platform: tenant, provider,
 *    name, masked hint, scope, priority, status, last used.
 *
 * Data source: GET /super-admin/api-keys/overview.
 */

import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Table, Tag, Tooltip, message as antdMessage } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';

interface OverviewKey {
  key_id: string;
  tenant_id: string;
  tenant_name: string;
  provider: string;
  key_name: string;
  key_hint: string;
  priority: number;
  key_scope: string;
  key_status: string;
  unhealthy_reason: string | null;
  unhealthy_at: string | null;
  last_used_at: string | null;
  created_at: string | null;
}

export function ApiKeysOverviewTab() {
  const { t } = useLanguage();
  const [keys, setKeys] = useState<OverviewKey[]>([]);
  const [incidents, setIncidents] = useState<OverviewKey[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/api-keys/overview');
      setKeys(res.data?.keys || []);
      setIncidents(res.data?.incidents || []);
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusTag = (record: OverviewKey) => {
    if (record.key_status === 'unhealthy') {
      return (
        <Tooltip title={record.unhealthy_reason || ''}>
          <Tag color="red" icon={<WarningOutlined />}>
            {t('superAdmin.apiKeys.statusUnhealthy')}
          </Tag>
        </Tooltip>
      );
    }
    if (record.key_status === 'disabled') {
      return <Tag>{t('superAdmin.apiKeys.statusDisabled')}</Tag>;
    }
    return <Tag color="green">{t('superAdmin.apiKeys.statusActive')}</Tag>;
  };

  return (
    <div data-testid="api-keys-overview">
      {incidents.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('superAdmin.apiKeys.incidentsTitle')}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {incidents.map(k => (
                <li key={k.key_id}>
                  <strong dir="auto">{k.tenant_name}</strong> — {k.provider} "{k.key_name}" (****{k.key_hint}):{' '}
                  {k.unhealthy_reason || t('superAdmin.apiKeys.statusUnhealthy')}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          {t('common.refresh')}
        </Button>
      </div>

      <Table<OverviewKey>
        size="small"
        rowKey="key_id"
        loading={loading}
        dataSource={keys}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: t('superAdmin.apiKeys.noKeys') }}
        columns={[
          {
            title: t('superAdmin.apiKeys.colTenant'),
            dataIndex: 'tenant_name',
            sorter: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
          },
          {
            title: t('superAdmin.apiKeys.colProvider'),
            dataIndex: 'provider',
            width: 110,
            filters: [
              { text: 'Anthropic', value: 'anthropic' },
              { text: 'OpenAI', value: 'openai' },
              { text: 'Google', value: 'google' },
            ],
            onFilter: (value, record) => record.provider === value,
          },
          {
            title: t('superAdmin.apiKeys.colName'),
            dataIndex: 'key_name',
            render: (name: string, record) => (
              <span>{name} <span style={{ opacity: 0.6 }}>****{record.key_hint}</span></span>
            ),
          },
          {
            title: t('superAdmin.apiKeys.colScope'),
            dataIndex: 'key_scope',
            width: 150,
            render: (scope: string) => (
              scope === 'subtree'
                ? <Tag color="blue">{t('superAdmin.apiKeys.scopeSubtreeShort')}</Tag>
                : <Tag>{t('superAdmin.apiKeys.scopeTenantOnlyShort')}</Tag>
            ),
          },
          { title: t('superAdmin.apiKeys.colPriority'), dataIndex: 'priority', width: 80 },
          {
            title: t('superAdmin.apiKeys.colStatus'),
            dataIndex: 'key_status',
            width: 120,
            filters: [
              { text: t('superAdmin.apiKeys.statusActive'), value: 'active' },
              { text: t('superAdmin.apiKeys.statusUnhealthy'), value: 'unhealthy' },
              { text: t('superAdmin.apiKeys.statusDisabled'), value: 'disabled' },
            ],
            onFilter: (value, record) => record.key_status === value,
            render: (_: string, record) => statusTag(record),
          },
          {
            title: t('superAdmin.apiKeys.colLastUsed'),
            dataIndex: 'last_used_at',
            width: 170,
            render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
          },
        ]}
      />
    </div>
  );
}
